import { useState } from "react";
import { ManaText } from "@/components/common/ManaSymbol";
import { ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { assetUrl } from "@/lib/api";
import { frameLabel } from "@/lib/planar";

// Cards in one row share a height; each keeps its true width.
const HEIGHT = { xl: "min(68vh, 46rem)", lg: "26rem", md: "15rem", sm: "9rem" };

export const shapeOf = (c) => (c.aspect ? { ratio: c.aspect, natural: true } : { ratio: c.orientation === "landscape" ? 1.4286 : 0.716, natural: false });

function Art({ c }) {
  const [rotate, setRotate] = useState(false);
  const { natural } = shapeOf(c);
  const src = assetUrl(c.image_url);
  if (!src) return <div className="flex h-full flex-col justify-center p-4 text-left"><p className="font-heading text-amber-100">{c.name}</p><p className="mt-2 line-clamp-6 text-xs text-slate-400"><ManaText text={c.oracle_text} /></p></div>;
  if (natural) return <img src={src} alt={c.name} draggable={false} className="h-full w-full object-contain" />;
  const onLoad = (e) => setRotate(c.orientation === "landscape" && e.target.naturalHeight > e.target.naturalWidth);
  return <img src={src} alt={c.name} onLoad={onLoad} draggable={false}
    className={rotate ? "absolute left-1/2 top-1/2 max-w-none object-fill" : "h-full w-full object-contain"}
    style={rotate ? { width: "70%", height: "142.86%", transform: "translate(-50%, -50%) rotate(90deg)" } : undefined} />;
}

function Details({ c }) {
  const { ratio } = shapeOf(c);
  return (
    <>
      <DialogTitle className="font-display text-2xl text-amber-100">{c.name}</DialogTitle>
      <div className="relative mx-auto overflow-hidden rounded-lg" style={{ aspectRatio: ratio, width: `min(100%, calc(60vh * ${ratio}))` }}><Art c={c} /></div>
      <p className="font-mono text-xs uppercase tracking-wider text-copper-300">{c.type_line}{c.design && <span className="ml-2 text-aether-300" data-testid="card-detail-design">· uploaded design · {frameLabel(c.design.visual_frame)}</span>}</p>
      <p className="whitespace-pre-line text-sm text-slate-200" data-testid="card-detail-text">{c.oracle_text ? <ManaText text={c.oracle_text} /> : "No rules text."}</p>
      {c.chaos_ability && <p className="whitespace-pre-line rounded-lg border border-amber-700/50 bg-amber-950/20 p-3 text-sm text-amber-100" data-testid="card-detail-chaos"><span className="font-mono text-xs uppercase text-amber-300">Chaos · </span>{c.chaos_ability}</p>}
      {(c.hand_modifier || c.life_modifier) && <p className="font-mono text-sm text-amber-200">Hand {c.hand_modifier || "+0"} · Life {c.life_modifier || "+0"}</p>}
      <p className="flex flex-wrap items-center gap-3 text-xs text-slate-500">{c.source === "custom" ? "Custom card by " : ""}{c.creator}
        {c.artist && <span data-testid="card-detail-artist">Art: {c.artist}</span>}
        {c.dimensions?.width_mm && <span>{c.dimensions.width_mm}×{c.dimensions.height_mm} mm</span>}
        {c.link && <a href={c.link} target={c.source === "official" ? "_blank" : undefined} rel="noreferrer" className="inline-flex items-center gap-1 text-aether-300 hover:underline">View card <ExternalLink className="h-3 w-3" /></a>}</p>
    </>
  );
}

export function CardFace({ c, size = "lg", testid, label }) {
  const [open, setOpen] = useState(false);
  if (!c) return null;
  const { ratio } = shapeOf(c);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid} data-aspect={ratio}
        className="group relative block max-w-full overflow-hidden rounded-xl border border-amber-700/60 bg-[#070B14] shadow-[0_0_45px_rgba(34,211,238,0.12)] transition-transform duration-300 hover:scale-[1.01]"
        style={{ aspectRatio: ratio, width: `calc(${HEIGHT[size]} * ${ratio})` }}>
        <Art c={c} />
        {label && <span className="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-aether-300">{label}</span>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto border-amber-700/50 bg-[#0B111E]" data-testid="card-detail-dialog"><Details c={c} /></DialogContent>
      </Dialog>
    </>
  );
}
